import { useState } from 'react';
import Button from '@/components/ui/Button';
import { ActionContext } from '@/types';

interface CommentInputProps {
onSubmit: (text: string, actionContext?: ActionContext) => Promise<void>;
placeholder?: string;
showActionContext?: boolean;
disabled?: boolean;
maxLength?: number;
}

const ACTION_CONTEXT_OPTIONS: { value: ActionContext; label: string }[] = [
{ value: 'status_change', label: 'Status Change' },
{ value: 'assignment', label: 'Assignment' },
{ value: 'approval', label: 'Approval' },
{ value: 'rejection', label: 'Rejection' },
{ value: 'expiration_update', label: 'Expiration Update' },
{ value: 'bulk_update', label: 'Bulk Update' },
{ value: 'cancellation', label: 'Cancellation' },
{ value: 'reopening', label: 'Reopening' },
{ value: 'completion', label: 'Completion' },
];

export function CommentInput({
onSubmit,
placeholder = 'Add a comment...',
showActionContext = false,
disabled = false,
maxLength = 5000,
}: CommentInputProps) {
const [text, setText] = useState('');
const [actionContext, setActionContext] = useState<ActionContext | undefined>();
const [isSubmitting, setIsSubmitting] = useState(false);
const [error, setError] = useState<string | null>(null);

const remainingChars = maxLength - text.length;
const isValid = text.trim().length > 0 && remainingChars >= 0;

const handleSubmit = async (e: React.FormEvent) => {
e.preventDefault();

if (!isValid || isSubmitting) return;

setIsSubmitting(true);
setError(null);

try {
await onSubmit(text.trim(), actionContext);
setText('');
setActionContext(undefined);
} catch (err) {
setError(err instanceof Error ? err.message : 'Failed to submit comment');
} finally {
setIsSubmitting(false);
}
};

return (
<form onSubmit={handleSubmit} className="space-y-3">
<div className="space-y-2">
<textarea
value={text}
onChange={(e) => setText(e.target.value)}
placeholder={placeholder}
disabled={disabled || isSubmitting}
maxLength={maxLength}
rows={3}
className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-outfront-purple disabled:bg-neutral-100 disabled:cursor-not-allowed resize-none"
/>

<div className="flex items-center justify-between text-sm">
<span className={`${remainingChars < 100 ? 'text-orange-600' : 'text-neutral-600'}`}>
{remainingChars} characters remaining
</span>
</div>
</div>

{showActionContext && (
<div className="space-y-1">
<label htmlFor="action-context" className="block text-sm font-medium text-neutral-600">
Action Context (Optional)
</label>
<select
id="action-context"
value={actionContext || ''}
onChange={(e) => setActionContext(e.target.value as ActionContext || undefined)}
disabled={disabled || isSubmitting}
className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-outfront-purple disabled:bg-neutral-100 disabled:cursor-not-allowed"
>
<option value="">None</option>
{ACTION_CONTEXT_OPTIONS.map((option) => (
<option key={option.value} value={option.value}>
{option.label}
</option>
))}
</select>
</div>
)}

{error && (
<div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
{error}
</div>
)}

<div className="flex justify-end">
<Button
type="submit"
disabled={!isValid || disabled || isSubmitting}
className="px-4 py-2"
>
{isSubmitting ? 'Submitting...' : 'Add Comment'}
</Button>
</div>
</form>
);
}
